import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { LuInstagram as Instagram } from 'react-icons/lu'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { handleInstagramCallback } from '@/lib/api'

export type InstagramConnected = Awaited<ReturnType<typeof handleInstagramCallback>>

const INSIGHTS_SCOPE = 'instagram_business_manage_insights'

const SHARED_BASICS = 'Your profile, follower count, posts, likes and comments'
const SHARED_INSIGHTS =
  'Who follows you (age, gender and location) and how each post performs (reach, shares and saves)'
import { useAuth } from '@/hooks/useAuth'

interface Props {
  connected: InstagramConnected
  onContinue: () => void
  onRetry: () => void
}

/** What the athlete sees after Instagram sends them back: which account, and what they allowed */
const InstagramConnectedCard = ({ connected, onContinue, onRetry }: Props) => {
  const limited = connected.missing_permissions.length > 0
  return (
    <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
      <Card variant="dark" className="overflow-hidden w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-linear-to-r from-[#FF3A89] to-[#FFC702] p-4 rounded-full">
              <Instagram className="h-8 w-8 text-[#FFFFFF]" />
            </div>
          </div>
          <CardTitle className="text-foreground text-xl">
            {limited ? 'Connected, with limited access' : 'Connected'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          <div className="space-y-5">
            <div className="flex items-center gap-4 rounded-xl border border-foreground/10 bg-card p-4 text-left shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
              <Avatar className="h-14 w-14">
                <AvatarImage src={connected.profile_picture_url ?? undefined} alt="" />
                <AvatarFallback className="bg-muted text-foreground">
                  {connected.username.slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">@{connected.username}</p>
                <p className="truncate text-sm text-gray-600">
                  {[
                    connected.name,
                    connected.followers != null
                      ? `${connected.followers.toLocaleString()} followers`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
            </div>

            {connected.missing_permissions.includes(INSIGHTS_SCOPE) ? (
              <Alert className="bg-amber-500/10 border-amber-500/20 text-left">
                <AlertCircle className="h-4 w-4 text-amber-700" />
                <AlertDescription className="space-y-2 text-amber-800">
                  <p className="font-medium text-amber-700">
                    You turned off access to insights on Instagram.
                  </p>
                  <p>
                    {SHARED_BASICS} are connected. Without insights, we can't see{' '}
                    {SHARED_INSIGHTS.charAt(0).toLowerCase() + SHARED_INSIGHTS.slice(1)}.
                  </p>
                  <p>
                    Brands use those numbers to decide who to work with, so profiles without them
                    are easier to pass over. We only read these numbers. We never post or message
                    for you.
                  </p>
                </AlertDescription>
              </Alert>
            ) : limited ? (
              <Alert className="bg-amber-500/10 border-amber-500/20 text-left">
                <AlertCircle className="h-4 w-4 text-amber-700" />
                <AlertDescription className="text-amber-800">
                  <p className="font-medium text-amber-700">
                    Some of the access we asked for was turned off on Instagram.
                  </p>
                  <p>
                    Reconnect and tap Allow on everything so your profile can be shown in full.
                  </p>
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-left text-sm text-gray-600">
                Connected: {SHARED_BASICS.charAt(0).toLowerCase() + SHARED_BASICS.slice(1)}, plus{' '}
                {SHARED_INSIGHTS.charAt(0).toLowerCase() + SHARED_INSIGHTS.slice(1)}. We only read
                these numbers. We never post or message for you.
              </p>
            )}

            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-center">
              {limited ? (
                <>
                  <Button
                    variant="outline"
                    onClick={onContinue}
                    className="border-foreground/20 text-gray-700 hover:bg-foreground/5"
                  >
                    Continue without insights
                  </Button>
                  <Button onClick={onRetry} variant="lime">
                    Reconnect and allow insights
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={onRetry}
                    className="border-foreground/20 text-gray-700 hover:bg-foreground/5"
                  >
                    Not your account?
                  </Button>
                  <Button onClick={onContinue} variant="lime">
                    Continue
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InstagramConnectedCard
